import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import type { CaseItem, ItemRarity } from '~/lib/contracts';

const FALLBACK_ITEM_STEP_WIDTH = 240;
const TRACK_REPEAT_COUNT = 100;
const SPIN_DURATION_MS = 4600;
const SPIN_LOOP_COUNT = 25; // Must be less than TRACK_REPEAT_COUNT
const LANDING_EDGE_EPSILON_PX = 0.5;
export const MAX_RARITY = 5;

// Augment the module to allow CSS variables
declare module 'react' {
    interface CSSProperties {
        [key: `--${string}`]: string | number;
    }
}

const rarityClassMap: Record<ItemRarity, string> = {
    0: 'border-zinc-400 bg-radial from-zinc-800 to-zinc-950/50',
    1: 'border-blue-500 bg-radial from-blue-800 to-blue-950/50',
    2: 'border-purple-500 bg-radial from-purple-800 to-purple-950/50',
    3: 'border-pink-500 bg-radial from-pink-800 to-pink-950/50',
    4: 'border-red-400 bg-radial from-red-800 to-red-950/50',
    5: 'border-yellow-300 bg-radial from-yellow-600 to-yellow-800/50',
};

const rarityFrequencyMap: Record<ItemRarity, number> = {
    0: 392,
    1: 440,
    2: 494,
    3: 554,
    4: 659,
    5: 784,
};

type RouletteProps = {
    items: CaseItem[];
};

export function Roulette({ items }: RouletteProps) {
    const [isSpinning, setIsSpinning] = useState(false);
    const [activeResult, setActiveResult] = useState<CaseItem | null>(null);
    const [isResultModalOpen, setIsResultModalOpen] = useState(false);
    const [spinOffset, setSpinOffset] = useState(0);
    const [trackAnimation, setTrackAnimation] = useState(false);
    const audioContextRef = useRef<AudioContext | null>(null);
    const trackRef = useRef<HTMLDivElement | null>(null);
    const monitorFrameRef = useRef<number | null>(null);
    const targetOffsetRef = useRef(0);
    const lastTickStepRef = useRef(0);
    const currentStepWidthRef = useRef(FALLBACK_ITEM_STEP_WIDTH);
    const currentViewportWidthRef = useRef(0);

    const orderedItems = useMemo(() => {
        if (items.length === 0) {
            return [];
        }

        const nextItems = [...items];
        const randomBuffer = new Uint32Array(nextItems.length);
        crypto.getRandomValues(randomBuffer);

        for (let index = nextItems.length - 1; index > 0; index -= 1) {
            const randomValue = randomBuffer[index];
            if (randomValue === undefined) {
                continue;
            }

            const swapIndex = randomValue % (index + 1);
            const currentItem = nextItems[index];
            const swapItem = nextItems[swapIndex];
            if (!currentItem || !swapItem) {
                continue;
            }

            nextItems[index] = swapItem;
            nextItems[swapIndex] = currentItem;
        }

        return nextItems;
    }, [items]);

    const trackItems = useMemo(() => {
        if (orderedItems.length === 0) {
            return [];
        }

        return Array.from({ length: orderedItems.length * TRACK_REPEAT_COUNT }, (_, index) => {
            const poolItem = orderedItems[index % orderedItems.length];
            if (!poolItem) {
                throw new Error('Roulette item pool cannot be empty');
            }

            return {
                ...poolItem,
                key: `${poolItem.id}-${index}`,
            };
        });
    }, [orderedItems]);

    useEffect(() => {
        stopTickMonitor();
        setTrackAnimation(false);
        setSpinOffset(0);
        setIsSpinning(false);
        setActiveResult(null);
        setIsResultModalOpen(false);
    }, [items]);

    useEffect(() => {
        return () => {
            const frameId = monitorFrameRef.current;
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
                monitorFrameRef.current = null;
            }

            const audioContext = audioContextRef.current;
            if (!audioContext) {
                return;
            }

            void audioContext.close();
            audioContextRef.current = null;
        };
    }, []);

    const getAudioContext = () => {
        const existingContext = audioContextRef.current;
        if (existingContext) {
            return existingContext;
        }

        const nextContext = new AudioContext();
        audioContextRef.current = nextContext;
        return nextContext;
    };

    const playTone = (frequency: number, durationSeconds: number, type: OscillatorType, gainValue: number) => {
        const audioContext = getAudioContext();
        if (audioContext.state === 'suspended') {
            void audioContext.resume();
        }

        const now = audioContext.currentTime;
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.type = type;
        oscillator.frequency.setValueAtTime(frequency, now);

        gainNode.gain.setValueAtTime(0.0001, now);
        gainNode.gain.exponentialRampToValueAtTime(gainValue, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.0001, now + durationSeconds);

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.start(now);
        oscillator.stop(now + durationSeconds);
    };

    const stopTickMonitor = () => {
        const frameId = monitorFrameRef.current;
        if (frameId === null) {
            return;
        }

        cancelAnimationFrame(frameId);
        monitorFrameRef.current = null;
    };

    const getTrackOffset = () => {
        const trackElement = trackRef.current;
        if (!trackElement) {
            return 0;
        }

        const transform = window.getComputedStyle(trackElement).transform;
        if (transform === 'none') {
            return 0;
        }

        const matrix = new DOMMatrixReadOnly(transform);
        return Math.max(0, -matrix.m41);
    };

    const getItemStepWidth = () => {
        const trackElement = trackRef.current;
        const firstItem = trackElement?.firstElementChild as HTMLDivElement | null;
        if (!trackElement || !firstItem) {
            return FALLBACK_ITEM_STEP_WIDTH;
        }

        const itemStyles = window.getComputedStyle(firstItem);
        const marginLeft = Number.parseFloat(itemStyles.marginLeft) || 0;
        const marginRight = Number.parseFloat(itemStyles.marginRight) || 0;
        const stepWidth = firstItem.getBoundingClientRect().width + marginLeft + marginRight;
        if (stepWidth <= 0) {
            return FALLBACK_ITEM_STEP_WIDTH;
        }

        return stepWidth;
    };

    const getViewportWidth = () => {
        const trackElement = trackRef.current;
        const viewportElement = trackElement?.parentElement;
        if (!viewportElement) {
            return 0;
        }

        return viewportElement.getBoundingClientRect().width;
    };

    const monitorTickTouches = () => {
        const currentOffset = getTrackOffset();
        const probePosition = currentOffset + currentViewportWidthRef.current / 2;
        const nextStep = Math.floor(probePosition / currentStepWidthRef.current);

        if (nextStep > lastTickStepRef.current) {
            for (let step = lastTickStepRef.current + 1; step <= nextStep; step += 1) {
                playTone(1400, 0.018, 'square', 0.03);
            }

            lastTickStepRef.current = nextStep;
        }

        if (currentOffset + 0.5 >= targetOffsetRef.current) {
            stopTickMonitor();
            return;
        }

        monitorFrameRef.current = requestAnimationFrame(monitorTickTouches);
    };

    const spin = () => {
        if (isSpinning || orderedItems.length === 0 || trackItems.length === 0) {
            return;
        }

        const randomBuffer = new Uint32Array(2);
        crypto.getRandomValues(randomBuffer);
        const winnerValue = randomBuffer[0];
        const landingValue = randomBuffer[1];
        if (winnerValue === undefined || landingValue === undefined) {
            return;
        }

        const weightedPool = orderedItems.map((item, index) => ({
            index,
            weight: Math.pow(2, MAX_RARITY - item.rarity),
        }));
        const totalWeight = weightedPool.reduce((sum, item) => sum + item.weight, 0);
        if (totalWeight <= 0) {
            return;
        }

        const randomWeightPoint = winnerValue % totalWeight;
        let accumulatedWeight = 0;
        let randomIndex = weightedPool[0]?.index ?? 0;

        for (const weightedItem of weightedPool) {
            accumulatedWeight += weightedItem.weight;
            if (randomWeightPoint < accumulatedWeight) {
                randomIndex = weightedItem.index;
                break;
            }
        }

        const targetIndex = orderedItems.length * SPIN_LOOP_COUNT + randomIndex;
        const stepWidth = getItemStepWidth();
        const viewportWidth = getViewportWidth();
        const landingUnit = landingValue / 0x100000000;
        const rawLandingOffsetWithinItem = stepWidth * landingUnit;
        const edgePadding = Math.min(LANDING_EDGE_EPSILON_PX, stepWidth / 2);
        const landingOffsetWithinItem = Math.min(
            stepWidth - edgePadding,
            Math.max(edgePadding, rawLandingOffsetWithinItem),
        );
        const targetOffset = Math.max(0, targetIndex * stepWidth + landingOffsetWithinItem - viewportWidth / 2);
        const targetItem = trackItems[targetIndex];
        if (!targetItem) {
            return;
        }

        setActiveResult(null);
        setIsResultModalOpen(false);
        setIsSpinning(true);
        setTrackAnimation(false);
        setSpinOffset(0);
        stopTickMonitor();
        currentStepWidthRef.current = stepWidth;
        currentViewportWidthRef.current = viewportWidth;
        targetOffsetRef.current = targetOffset;
        lastTickStepRef.current = Math.floor((viewportWidth / 2) / stepWidth);
        playTone(262, 0.12, 'triangle', 0.08);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                setTrackAnimation(true);
                setSpinOffset(targetOffset);
                monitorFrameRef.current = requestAnimationFrame(monitorTickTouches);
            });
        });

        window.setTimeout(() => {
            stopTickMonitor();
            setActiveResult(targetItem);
            setIsResultModalOpen(true);
            setIsSpinning(false);
            playTone(rarityFrequencyMap[targetItem.rarity], 0.24, 'sine', 0.1);
        }, SPIN_DURATION_MS);
    };

    return (
        <div>
            <div className="relative overflow-clip">
                <div className="pointer-events-none absolute inset-y-0 left-1/2 z-20 w-0.5 -translate-x-1/2 bg-orange-400 shadow-[0_0_16px_rgba(251,146,60,0.9)]" />

                <div className="aspect-square -translate-y-1/3 pointer-events-none absolute inset-0 z-10 bg-radial from-transparent from-40% to-80% to-neutral-950/90" />

                <div
                    ref={trackRef}
                    className={`flex py-5 will-change-transform transition-transform ${trackAnimation ? 'ease-[cubic-bezier(0.06,0.80,0.18,1.00)]' : 'ease-linear'} `}
                    style={{
                        transform: `translateX(-${spinOffset}px)`,
                        transitionDuration: `${trackAnimation ? SPIN_DURATION_MS : 0}ms`,
                        '--tw-duration': `${trackAnimation ? SPIN_DURATION_MS : 0}ms`,
                    }}
                >
                    {trackItems.map((item) => (
                        <div
                            key={item.key}
                            className={`flex gap-2 min-h-56 w-56 shrink-0 flex-col items-center justify-center border-y-4 p-2 ${rarityClassMap[item.rarity]}`}
                        >
                            {item.imageUrl && (
                                <img
                                    src={item.imageUrl}
                                    alt={item.name}
                                    className="aspect-square rounded object-cover object-center"
                                />
                            )}
                            <p className="truncate text-center font-semibold text-white">
                                {item.name}
                            </p>
                        </div>
                    ))}
                </div>
            </div>

            <div className="w-full mt-4 flex justify-center gap-4">
                <Button onClick={spin} disabled={isSpinning || items.length === 0} size="lg" className="min-w-36">
                    {isSpinning ? 'Spinning...' : 'Spin'}
                </Button>
            </div>
            {items.length === 0 && (
                <p className="mt-3 text-center text-sm text-neutral-300">No items in this case.</p>
            )}

            <Dialog open={isResultModalOpen} onOpenChange={setIsResultModalOpen}>
                <DialogContent className={`max-w-md border-2 ${rarityClassMap[activeResult?.rarity ?? 0]}`}>
                    <DialogHeader>
                        <DialogTitle>{activeResult?.name}</DialogTitle>
                    </DialogHeader>

                    {activeResult?.imageUrl && (
                        <img
                            src={activeResult.imageUrl}
                            alt={activeResult.name}
                            className="mb-3 aspect-square w-full rounded object-cover object-center"
                        />
                    )}

                </DialogContent>
            </Dialog>
        </div>

    );
}
